import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './Icon';

/* ============================================================
   In-app Toast system — imperative API via context, never the
   native alert(). Ported from the prototype's ui.jsx.
   ============================================================ */

type ToastTone = 'sage' | 'spark' | 'clay' | 'slate';
interface ToastItem {
  id: number;
  msg: ReactNode;
  tone: ToastTone;
  icon: IconName;
}

interface ToastApi {
  toast: (msg: ReactNode, opts?: { tone?: ToastTone; icon?: IconName }) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

const TONE_COLOR: Record<ToastTone, string> = {
  sage: 'var(--sage)',
  spark: 'var(--spark)',
  clay: 'var(--clay)',
  slate: 'var(--slate)',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const toast = useCallback<ToastApi['toast']>((msg, opts) => {
    const id = ++seq.current;
    const item: ToastItem = { id, msg, tone: opts?.tone ?? 'sage', icon: opts?.icon ?? 'check' };
    setItems((xs) => [...xs, item]);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), 2600);
  }, []);

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        style={{
          position: 'fixed',
          bottom: 22,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 200,
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="animate-toastIn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'var(--ink)',
              color: 'var(--paper)',
              padding: '11px 16px 11px 13px',
              borderRadius: 99,
              boxShadow: 'var(--sh-lg)',
              fontSize: 14,
              fontWeight: 500,
              maxWidth: '88vw',
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 99,
                background: TONE_COLOR[t.tone],
                color: 'var(--on-accent)',
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
              }}
            >
              <Icon name={t.icon} size={14} stroke={2.2} />
            </span>
            {t.msg}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
